import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Landmark, Home } from "lucide-react";

/**
 * Address / venue autocomplete.
 *
 * Combines two sources in parallel to catch both street addresses and named
 * venues (châteaux, domaines, salons, salles de réception, restaurants…) :
 *   - api-adresse.data.gouv.fr (BAN) — precise French postal addresses
 *   - photon.komoot.io — OpenStreetMap-backed venues & POI (worldwide)
 *
 * On select, calls onSelect({ label, street, postcode, city, kind }) AND
 * onChange with the FULL label (not just street) so the form stores the
 * complete address including postal code + city.
 *
 * Props:
 *   value, onChange, onSelect, placeholder, required, testId
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Adresse, nom de la salle, château, domaine…",
  required = false,
  testId = "address-autocomplete",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const parseBAN = (data) => {
    const feats = Array.isArray(data?.features) ? data.features : [];
    return feats.map((f) => {
      const p = f?.properties || {};
      const street = p.name || "";
      const postcode = p.postcode || "";
      const city = p.city || "";
      const label = p.label || [street, postcode, city].filter(Boolean).join(" ");
      return {
        label,
        line1: street,               // just the street (BAN never has POI names)
        street,
        postcode,
        city,
        context: p.context || "",
        kind: "address",
        score: typeof p.score === "number" ? p.score : 0.5,
      };
    });
  };

  const parsePhoton = (data) => {
    const feats = Array.isArray(data?.features) ? data.features : [];
    return feats.map((f) => {
      const p = f?.properties || {};
      const name = p.name || "";
      const housenumber = p.housenumber || "";
      const streetName = p.street || "";
      const postcode = p.postcode || "";
      const city = p.city || p.town || p.village || "";
      const country = p.country || "";
      const state = p.state || "";
      const streetLine = [housenumber, streetName].filter(Boolean).join(" ");
      // Build full label: name first (venue), then address
      const parts = [];
      if (name && name !== streetLine) parts.push(name);
      if (streetLine) parts.push(streetLine);
      const cityPart = [postcode, city].filter(Boolean).join(" ");
      if (cityPart) parts.push(cityPart);
      if (country && country !== "France") parts.push(country);
      const label = parts.join(", ") || name;
      // line1 = everything the user should see in the "adresse" field
      // (venue name + street), but WITHOUT postcode/city (they have dedicated fields).
      const line1Parts = [];
      if (name && name !== streetLine) line1Parts.push(name);
      if (streetLine) line1Parts.push(streetLine);
      const line1 = line1Parts.join(", ") || name || streetLine;
      const kind = p.osm_key === "highway" || p.osm_value === "yes" ? "address" : "venue";
      return {
        label,
        line1,
        street: streetLine || name,
        postcode,
        city,
        context: [state, country].filter(Boolean).join(" · "),
        kind,
        score: p.osm_id ? 0.7 : 0.5,
      };
    });
  };

  const fetchSuggestions = (q) => {
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const seq = ++requestSeqRef.current;
    setLoading(true);

    // Query BOTH APIs in parallel
    const banUrl = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6&autocomplete=1`;
    // Photon: fuzzy search, biased around Bousbecque (50.72, 3.15) → Nord/Belgique first
    const photonUrl = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lat=50.72&lon=3.15&lang=fr`;

    Promise.allSettled([
      fetch(banUrl).then((r) => r.ok ? r.json() : {}),
      fetch(photonUrl).then((r) => r.ok ? r.json() : {}),
    ])
      .then(([ban, photon]) => {
        if (seq !== requestSeqRef.current) return; // stale response
        const banRows = ban.status === "fulfilled" ? parseBAN(ban.value) : [];
        const photonRows = photon.status === "fulfilled" ? parsePhoton(photon.value) : [];

        // Merge & dedupe by normalized label
        const seen = new Set();
        const merged = [];
        for (const row of [...banRows, ...photonRows]) {
          const key = row.label.toLowerCase().replace(/\s+/g, " ").trim();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(row);
        }
        // Sort: addresses first (higher score), then venues
        merged.sort((a, b) => (b.score || 0) - (a.score || 0));

        setSuggestions(merged.slice(0, 8));
        setOpen(merged.length > 0);
        setHighlight(-1);
      })
      .catch(() => { setSuggestions([]); setOpen(false); })
      .finally(() => { if (seq === requestSeqRef.current) setLoading(false); });
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 220);
  };

  const pick = (s) => {
    // Fill the input with a sensible default (line1 = venue + street, without CP/ville)
    onChange?.(s.line1 || s.street || s.label);
    // Then let the parent override if needed (e.g. the "lieu" field wants the full label)
    onSelect?.(s);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <input
        className="input-luxe pr-10"
        required={required}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => value && value.length >= 3 && suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={`${testId}-input`}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70 pointer-events-none">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} strokeWidth={1.4} />}
      </span>
      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          className="absolute z-30 left-0 right-0 mt-1 bg-[#0a0a0a] border border-[#DCB457]/30 max-h-[420px] overflow-y-auto shadow-2xl"
        >
          {suggestions.map((s, i) => {
            const Icon = s.kind === "venue" ? Landmark : Home;
            const isHi = highlight === i;
            return (
              <li key={`${s.label}-${i}`}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                  onMouseEnter={() => setHighlight(i)}
                  data-testid={`${testId}-suggestion-${i}`}
                  className={`w-full text-left px-3.5 py-2.5 text-sm flex items-start gap-2.5 transition-colors ${
                    isHi ? "bg-[#DCB457]/12 text-[#DCB457]" : "text-white/90 hover:bg-white/[0.04]"
                  }`}
                >
                  <Icon
                    size={14}
                    strokeWidth={1.4}
                    className={`mt-0.5 shrink-0 ${s.kind === "venue" ? "text-emerald-300" : "text-[#DCB457]"}`}
                  />
                  <span className="flex-1 min-w-0">
                    {/* Full label — allow up to 2 lines, no truncation */}
                    <span className="block leading-snug break-words">
                      {s.label}
                    </span>
                    {s.context && (
                      <span className="block text-[10px] text-white/45 uppercase tracking-[0.14em] mt-1 break-words">
                        {s.kind === "venue" ? "Lieu · " : ""}{s.context}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
